import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { getCoverUrl, getMusicUrl, isBrokenCover, getProfilePhotoUrl } from "@/lib/mediaUrls";
import AppInstallsBySourceCard from "@/components/admin/AppInstallsBySourceCard";
import AppPromotionControlsCard from "@/components/admin/AppPromotionControlsCard";
import PromotionControlCenter from "@/components/admin/PromotionControlCenter";
import { FanChoiceIcon } from "@/components/FanChoiceIcon";

const STATUSES = ["submitted", "under_review", "shortlisted", "contacted", "featured", "rejected"];
const GENRES = ["Hip Hop", "Rap", "Trap", "R&B", "Pop", "Afrobeats", "Dancehall", "Reggae", "Drill", "Alternative", "Other"];

// User badge types for admin management
const USER_BADGE_TYPES = [
  { key: 'founding_artist', label: 'Founding Artist', icon: '⭐', activeClass: 'bg-gradient-to-r from-purple-500 to-pink-600 text-white' },
  { key: 'spotlight_winner', label: 'Spotlight Winner', icon: '🏆', activeClass: 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' },
  { key: 'spotlight_artist', label: 'Spotlight Artist', icon: '🔥', activeClass: 'bg-gradient-to-r from-orange-500 to-red-500 text-white' },
  { key: 'playlist_pick', label: 'Playlist Pick', icon: '🎵', activeClass: 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' },
  { key: 'rising_artist', label: 'Rising Artist', icon: '📈', activeClass: 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' },
  { key: 'verified_artist', label: 'Verified', icon: '✓', activeClass: 'bg-blue-500 text-white' },
  { key: 'producer', label: 'Producer', icon: '🎧', activeClass: 'bg-blue-600 text-white' }
];

// Track badge types
const TRACK_BADGE_TYPES = [
  { key: 'spotlight_artist', label: 'Spotlight', icon: '🔥', activeClass: 'bg-gradient-to-r from-orange-500 to-red-500 text-white' },
  { key: 'playlist_pick', label: 'Playlist Pick', icon: '🎵', activeClass: 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' },
  { key: 'rising_artist', label: 'Rising', icon: '📈', activeClass: 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' },
  { key: 'staff_pick', label: 'Staff Pick', icon: '⭐', activeClass: 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black' }
];

// Legacy for backward compatibility
const BADGE_TYPES = USER_BADGE_TYPES;

const SCORE_CATEGORIES = [
  { key: 'lyrics', label: 'Lyrics', max: 10, description: 'Quality of lyrics, storytelling, wordplay' },
  { key: 'flow', label: 'Flow', max: 10, description: 'Rhythm, delivery, cadence' },
  { key: 'originality', label: 'Originality', max: 10, description: 'Uniqueness, creativity, innovation' },
  { key: 'mix_quality', label: 'Mix Quality', max: 10, description: 'Production, mixing, mastering quality' },
  { key: 'branding', label: 'Branding', max: 10, description: 'Artist image, presentation, cover art' },
  { key: 'growth_potential', label: 'Growth Potential', max: 10, description: 'Market potential, audience appeal' }
];

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('submissions');
  const [filters, setFilters] = useState({
    genre: "",
    status: "",
    week: "",
    // Phase 9.7.14 — review queue workflow filters
    sort: "newest",            // newest | trending | high-engagement | collabs | spotlight-candidates
    search: "",
  });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [scores, setScores] = useState({
    lyrics: 0,
    flow: 0,
    originality: 0,
    mix_quality: 0,
    branding: 0,
    growth_potential: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Spotlight management state
  const [spotlights, setSpotlights] = useState([]);
  const [activeSpotlight, setActiveSpotlight] = useState(null);
  const [eligibleTracks, setEligibleTracks] = useState([]);
  const [spotlightForm, setSpotlightForm] = useState({
    week_label: "",
    week_start: "",
    week_end: "",
    winner1: "",
    winner2: "",
    winner3: ""
  });
  const [savingSpotlight, setSavingSpotlight] = useState(false);

  // Verification requests state
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationFilter, setVerificationFilter] = useState('pending');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Repair Covers tool state
  const [repairScanning, setRepairScanning] = useState(false);
  const [repairRunning, setRepairRunning] = useState(false);
  const [repairResult, setRepairResult] = useState(null);  // last response payload
  const [repairLastRun, setRepairLastRun] = useState(null);  // persisted last real run summary

  // Phase 6 Part 2: Scan Assets (Rehydrate) tool state
  const [scanAssetsRunning, setScanAssetsRunning] = useState(false);
  const [scanAssetsResult, setScanAssetsResult] = useState(null);
  const [scanAssetsKindFilter, setScanAssetsKindFilter] = useState('all');

  // Phase 6 Critical: Find Duplicate Emails tool state
  const [dupeScanRunning, setDupeScanRunning] = useState(false);
  const [dupeScanResult, setDupeScanResult] = useState(null);

  // #11 Platform Health (counts, storage, integrity)
  const [platformHealth, setPlatformHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get(`/auth/me`);
        const role = response.data.role;
        const badges = response.data.badges || [];
        // Phase 6 Part 2: A&R (ar) can access the review queue + spotlight
        // read-only tools, but NOT admin-only tabs (users, tools).
        // Phase 9.7.8 — defensive: also accept the `ar` badge even if the
        // role wasn't flipped (matches backend get_ar_or_admin behaviour).
        const isAR = role === "ar" || badges.includes("ar");
        const isAdmin = role === "admin" || role === "moderator" ||
                        badges.includes("admin") || badges.includes("founder");
        if (!isAR && !isAdmin) {
          toast.error("Admin access required");
          navigate('/dashboard');
          return;
        }
        // Derive an effective role for downstream UI gating so the rest of
        // the page can keep using `user.role === 'ar'` etc.
        const effectiveRole = isAdmin ? (role === "admin" || role === "moderator" ? role : "admin")
                              : "ar";
        setUser({ ...response.data, role: effectiveRole });
        await fetchSubmissions();
        if (isAdmin) {
          await fetchUsers();
        }
        await fetchSpotlights();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const response = await api.get(`/submissions/all`);
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to fetch submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchSpotlights = async () => {
    try {
      const [spotlightsRes, activeRes, eligibleRes] = await Promise.all([
        api.get('/admin/spotlight'),
        api.get('/admin/spotlight/active'),
        api.get('/admin/spotlight/eligible-tracks')
      ]);
      setSpotlights(spotlightsRes.data || []);
      setActiveSpotlight(activeRes.data || null);
      setEligibleTracks(eligibleRes.data || []);
      console.log('Eligible tracks loaded:', eligibleRes.data?.length || 0);
    } catch (error) {
      console.error('Failed to fetch spotlights:', error);
      toast.error('Failed to load spotlight data');
    }
  };

  const handleAutoSuggestWinners = async () => {
    try {
      const res = await api.get('/admin/spotlight/auto-suggest');
      const suggestions = res.data?.suggestions || [];
      if (suggestions.length === 0) {
        toast.error('No tracks available to suggest');
        return;
      }
      // Auto-fill the form with up to 3 suggested submission_ids
      setSpotlightForm(prev => ({
        ...prev,
        winner1: suggestions[0]?.submission_id || prev.winner1,
        winner2: suggestions[1]?.submission_id || prev.winner2,
        winner3: suggestions[2]?.submission_id || prev.winner3,
      }));
      toast.success(`Suggested ${suggestions.length} winners — review and publish when ready`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-suggest failed');
    }
  };

  const handlePublishSpotlight = async (spotlightId) => {
    try {
      await api.post(`/admin/spotlight/${spotlightId}/publish`);
      toast.success('Spotlight published — visible on Discovery & landing');
      await fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Publish failed');
    }
  };

  const handleUnpublishSpotlight = async (spotlightId) => {
    try {
      await api.post(`/admin/spotlight/${spotlightId}/unpublish`);
      toast.success('Spotlight hidden from public');
      await fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unpublish failed');
    }
  };

  const fetchVerificationRequests = async (statusFilter = verificationFilter) => {
    try {
      const response = await api.get(`/admin/verification/requests?status=${statusFilter}`);
      setVerificationRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch verification requests:', error);
      toast.error('Failed to load verification requests');
    }
  };

  const handleApproveVerification = async (requestId) => {
    if (!window.confirm('Approve this verification request? The user will receive the Verified badge.')) return;
    try {
      await api.post(`/admin/verification/${requestId}/approve`);
      toast.success('Verification approved');
      await fetchVerificationRequests();
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve verification');
    }
  };

  const handleRejectVerification = async (requestId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await api.post(`/admin/verification/${requestId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Verification rejected');
      setRejectingId(null);
      setRejectReason("");
      await fetchVerificationRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject verification');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and ALL their data? This action cannot be undone.')) return;

    try {
      await api.delete(`/admin/user/${userId}`);
      toast.success('User deleted successfully');
      await fetchUsers();
      await fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // ---------- Repair Covers tool ----------
  const fetchRepairLastRun = async () => {
    try {
      const r = await api.get(`/admin/repair-covers/last-run`);
      setRepairLastRun(r.data || null);
    } catch (err) {
      // Non-fatal — just don't show the indicator
      setRepairLastRun(null);
    }
  };

  // ---------- #11 Platform Health ----------
  const fetchPlatformHealth = async () => {
    setHealthLoading(true);
    try {
      const r = await api.get(`/admin/platform-health`);
      setPlatformHealth(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load platform health');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleScanCovers = async () => {
    setRepairScanning(true);
    try {
      const r = await api.post(`/admin/repair-covers?dry_run=true`);
      setRepairResult(r.data);
      toast.success(
        `Scan complete — ${r.data.broken} broken cover(s) detected of ${r.data.scanned} scanned.`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setRepairScanning(false);
    }
  };

  const handleRepairCovers = async () => {
    if (!window.confirm(
      'Repair the next batch of broken covers? This will re-upload local files to Cloudinary and update the database. Records without a local file will be skipped. Up to 50 covers will be repaired per click — re-click for more if any remain.'
    )) return;
    setRepairRunning(true);
    try {
      const r = await api.post(`/admin/repair-covers`);
      setRepairResult(r.data);
      const more = r.data.more_remaining ? ` — ${r.data.more_remaining} still remaining, click again` : '';
      toast.success(
        `Repair batch complete — ${r.data.repaired} repaired, ${r.data.failed} failed, ${r.data.skipped_missing_local_file} skipped${more}.`
      );
      // Refresh the "Last repair run" indicator
      fetchRepairLastRun();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairRunning(false);
    }
  };

  // Phase 6 Part 2: Rehydrate / Scan Assets tool
  const handleScanAssets = async () => {
    setScanAssetsRunning(true);
    setScanAssetsResult(null);
    try {
      const r = await api.get(`/admin/scan-assets`);
      setScanAssetsResult(r.data);
      toast.success(
        `Scan complete — ${r.data.flagged_count} flagged records across ${Object.values(r.data.scanned || {}).reduce((a,b) => a+b, 0)} docs.`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanAssetsRunning(false);
    }
  };

  // Phase 6 Critical: Find Duplicate Emails (scan-only)
  const [mergingUserId, setMergingUserId] = useState(null);
  const handleScanDuplicateEmails = async () => {
    setDupeScanRunning(true);
    setDupeScanResult(null);
    try {
      const r = await api.get(`/admin/users/duplicate-emails`);
      setDupeScanResult(r.data);
      const groupCount = (r.data.duplicate_groups || []).length;
      const userCount = (r.data.duplicate_groups || []).reduce((a, g) => a + (g.count || 0), 0);
      if (groupCount === 0) {
        toast.success('No duplicate emails found 🎉');
      } else {
        toast.success(`Found ${groupCount} duplicate group${groupCount > 1 ? 's' : ''} (${userCount} users).`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setDupeScanRunning(false);
    }
  };

  const handleMergeDuplicateUser = async (duplicateUserId, primaryUserId, dupeLabel, primaryLabel) => {
    if (!duplicateUserId || !primaryUserId) return;
    if (duplicateUserId === primaryUserId) {
      toast.error("Can't merge an account into itself");
      return;
    }
    const ok = window.confirm(
      `This will move ${dupeLabel || duplicateUserId}'s data (submissions, comments, follows, owned crews/clubs/playlists/businesses) into ${primaryLabel || primaryUserId}.\n\nThis cannot be undone.\n\nProceed?`
    );
    if (!ok) return;
    setMergingUserId(duplicateUserId);
    try {
      const r = await api.post(`/admin/users/${duplicateUserId}/merge-into/${primaryUserId}`);
      const moved = r.data?.moved || {};
      const totalMoved = Object.values(moved).reduce((a, b) => a + (Number(b) || 0), 0);
      toast.success(`Merged. ${totalMoved} record${totalMoved === 1 ? '' : 's'} re-attributed.`);
      // Refresh the scan so the merged user disappears from the list
      await handleScanDuplicateEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingUserId(null);
    }
  };

  const handleToggleBadge = async (userId, badgeType) => {
    try {
      const response = await api.post(`/admin/user/${userId}/badge/${badgeType}`);
      toast.success(response.data.message);
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update badge');
    }
  };

  // Spotlight management functions
  const handleCreateSpotlight = async () => {
    if (!spotlightForm.week_label || !spotlightForm.week_start || !spotlightForm.week_end) {
      toast.error("Please fill in week label and dates");
      return;
    }
    
    if (!spotlightForm.winner1) {
      toast.error("Please select at least the #1 winner");
      return;
    }
    
    const winners = [];
    if (spotlightForm.winner1) winners.push({ submission_id: spotlightForm.winner1, position: 1 });
    if (spotlightForm.winner2) winners.push({ submission_id: spotlightForm.winner2, position: 2 });
    if (spotlightForm.winner3) winners.push({ submission_id: spotlightForm.winner3, position: 3 });
    
    setSavingSpotlight(true);
    try {
      await api.post('/admin/spotlight', {
        week_label: spotlightForm.week_label,
        week_start: new Date(spotlightForm.week_start).toISOString(),
        week_end: new Date(spotlightForm.week_end).toISOString(),
        winners
      });
      toast.success("Spotlight created and activated!");
      setSpotlightForm({ week_label: "", week_start: "", week_end: "", winner1: "", winner2: "", winner3: "" });
      await fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create spotlight");
    } finally {
      setSavingSpotlight(false);
    }
  };

  const handleActivateSpotlight = async (spotlightId) => {
    try {
      await api.post(`/admin/spotlight/${spotlightId}/activate`);
      toast.success("Spotlight activated!");
      await fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate spotlight");
    }
  };

  const handleDeleteSpotlight = async (spotlightId) => {
    if (!window.confirm("Delete this spotlight week?")) return;
    
    try {
      await api.delete(`/admin/spotlight/${spotlightId}`);
      toast.success("Spotlight deleted");
      await fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete spotlight");
    }
  };

  useEffect(() => {
    let filtered = [...submissions];

    if (filters.genre) {
      filtered = filtered.filter(s => s.genre === filters.genre);
    }
    if (filters.status) {
      filtered = filtered.filter(s => s.status === filters.status);
    }
    if (filters.week) {
      filtered = filtered.filter(s => s.week_number === parseInt(filters.week));
    }
    // Phase 9.7.14 — review queue workflow filters
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(s =>
        (s.track_title || "").toLowerCase().includes(q) ||
        (s.artist_name || "").toLowerCase().includes(q) ||
        (s.genre || "").toLowerCase().includes(q)
      );
    }
    const engagement = (s) =>
      (s.play_count || 0) + (s.reaction_count || 0) * 3 +
      (s.like_count || 0) * 3 + (s.gift_coins || 0) / 10;
    const dateValue = (s) =>
      new Date(s.created_at || s.submitted_at || 0).getTime();
    switch (filters.sort) {
      case "trending":
        filtered.sort((a, b) => engagement(b) - engagement(a));
        break;
      case "high-engagement":
        filtered.sort((a, b) => engagement(b) - engagement(a));
        break;
      case "collabs":
        filtered = filtered.filter(s => (s.featured_artists || []).length > 0);
        filtered.sort((a, b) => dateValue(b) - dateValue(a));
        break;
      case "spotlight-candidates":
        filtered = filtered.filter(s =>
          (s.score || 0) >= 45 || engagement(s) > 100
        );
        filtered.sort((a, b) => (b.score || 0) - (a.score || 0));
        break;
      case "newest":
      default:
        filtered.sort((a, b) => dateValue(b) - dateValue(a));
        break;
    }

    setFilteredSubmissions(filtered);
  }, [filters, submissions]);

  const calculateTotalScore = (scoreObj) => {
    return Object.values(scoreObj).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
  };

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      const updateData = {
        status: newStatus || selectedSubmission.status,
        admin_notes: adminNotes,
        scores: scores,
        score: calculateTotalScore(scores)
      };

      await api.patch(`/submissions/${selectedSubmission.submission_id}`, updateData);

      toast.success("Submission updated successfully");
      setSelectedSubmission(null);
      setAdminNotes("");
      setNewStatus("");
      setScores({ lyrics: 0, flow: 0, originality: 0, mix_quality: 0, branding: 0, growth_potential: 0 });
      await fetchSubmissions();
    } catch (error) {
      toast.error("Failed to update submission");
    }
  };

  const handleDownloadFile = (type, filename) => {
    const url = `${BACKEND_URL}/api/files/${type}/${filename}`;
    window.open(url, '_blank');
  };

  const handleFeatureTrack = async (submissionId, currentStatus) => {
    if (!window.confirm(currentStatus === 'featured' 
      ? 'Unfeature this track?' 
      : 'Feature this track? It will be highlighted on the platform.'
    )) return;

    try {
      await api.post(`/admin/feature/${submissionId}`);
      toast.success(currentStatus === 'featured' ? 'Track unfeatured' : 'Track featured successfully');
      await fetchSubmissions();
    } catch (error) {
      toast.error('Failed to update track');
    }
  };

  // Phase 9.7.14 — quick Spotlight toggle from review row
  const handleSpotlightTrack = async (submissionId, currentStatus) => {
    const turningOn = currentStatus !== 'spotlight';
    if (!window.confirm(turningOn
      ? 'Set this track as Spotlight? It will receive the full spotlight surge.'
      : 'Remove this track from Spotlight?'
    )) return;
    try {
      await api.patch(`/submissions/${submissionId}`, {
        status: turningOn ? 'spotlight' : 'approved',
      });
      toast.success(turningOn ? 'Track marked Spotlight' : 'Spotlight removed');
      await fetchSubmissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update spotlight status');
    }
  };

  const handleDeleteTrack = async (submissionId) => {
    if (!window.confirm('Delete this track? This action cannot be undone.')) return;

    try {
      await api.delete(`/admin/track/${submissionId}`);
      toast.success('Track deleted successfully');
      await fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const openEditModal = (submission) => {
    setSelectedSubmission(submission);
    setNewStatus(submission.status);
    setAdminNotes(submission.admin_notes || "");
    setScores(submission.scores || {
      lyrics: 0,
      flow: 0,
      originality: 0,
      mix_quality: 0,
      branding: 0,
      growth_potential: 0
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      under_review: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      shortlisted: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      contacted: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      featured: "bg-green-500/20 text-green-400 border-green-500/30",
      rejected: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return colors[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const getScoreColor = (score, max = 60) => {
    const percentage = (score / max) * 100;
    if (percentage >= 75) return "text-green-400";
    if (percentage >= 50) return "text-yellow-400";
    return "text-red-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1e' }}>
      {/* Shared Navigation */}
      <SharedNavigation currentUser={user} />
      
      {/* Admin Header */}
      <div className="pt-16 border-b border-gray-800" style={{ background: '#0f0f11' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-2xl font-bold text-white">
              {user?.role === 'ar' ? 'A&R Review Queue' : 'Admin Dashboard'}
            </h1>
            <div className="flex items-center gap-4">
              {user?.role === 'ar' && (
                <span
                  className="text-xs px-2 py-1 rounded-full bg-pink-500/15 border border-pink-500/40 text-pink-200"
                  data-testid="ar-role-badge"
                >
                  🎯 A&R · Review-only
                </span>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin/fan-vote">
                  <Button className="bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold" data-testid="admin-open-fan-vote">
                    <FanChoiceIcon size="sm" className="mr-2" testId="admin-fan-vote-icon" />Fan Vote
                  </Button>
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin/affiliates">
                  <Button
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold"
                    data-testid="admin-open-affiliate-program"
                  >
                    🤝 Affiliate Program
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/control-center">
                  <Button
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
                    data-testid="admin-open-control-center"
                  >
                    🛰️ Control Center
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/live-health">
                  <Button
                    variant="outline"
                    className="text-white border-pink-500/40 hover:bg-pink-500/10"
                    data-testid="admin-open-live-health"
                  >
                    📡 Live Health
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/notifications-health">
                  <Button
                    variant="outline"
                    className="text-white border-purple-500/40 hover:bg-purple-500/10"
                    data-testid="admin-open-notifications-health"
                  >
                    🔔 Notif Diagnostics
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/cloudinary">
                  <Button
                    variant="outline"
                    className="text-white border-amber-500/40 hover:bg-amber-500/10"
                    data-testid="admin-open-cloudinary-storage"
                  >
                    ☁️ Cloudinary Storage
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/singles-store">
                  <Button
                    variant="outline"
                    className="text-white border-emerald-500/40 hover:bg-emerald-500/10"
                    data-testid="admin-open-singles-store"
                  >
                    💿 Singles Store
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/creative/share-cards">
                  <Button
                    variant="outline"
                    className="text-white border-fuchsia-500/40 hover:bg-fuchsia-500/10"
                    data-testid="admin-open-share-card-control"
                  >
                    🎴 Share Card Control
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/creative/menu-organizer">
                  <Button
                    variant="outline"
                    className="text-white border-indigo-500/40 hover:bg-indigo-500/10"
                    data-testid="admin-open-menu-organizer"
                  >
                    🧭 Menu Organizer
                  </Button>
                </Link>
              )}
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin/magazine">
                  <Button variant="outline" className="text-white border-gray-700">Magazine</Button>
                </Link>
              )}
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 pb-2 flex-wrap">
            <button
              onClick={() => setActiveTab('submissions')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'submissions' 
                  ? 'text-white border-b-2 border-purple-500' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="tab-submissions"
            >
              {user?.role === 'ar' ? 'Review Queue' : 'Submissions'} ({submissions.length})
            </button>
            {(user?.role === 'admin' || user?.role === 'moderator') && (
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'users' 
                    ? 'text-white border-b-2 border-purple-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-users"
              >
                Users ({users.length})
              </button>
            )}
            <button
              onClick={() => {
                setActiveTab('spotlight');
                fetchSpotlights(); // Refresh spotlight data when tab is clicked
              }}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'spotlight' 
                  ? 'text-white border-b-2 border-purple-500' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="tab-spotlight"
            >
              🏆 Spotlight{user?.role === 'ar' ? ' (suggest)' : ''}
            </button>
            {(user?.role === 'admin' || user?.role === 'moderator') && (
              <button
                onClick={() => {
                  setActiveTab('verification');
                  fetchVerificationRequests();
                }}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'verification' 
                    ? 'text-white border-b-2 border-purple-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-verification"
              >
                ✓ Verification
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'moderator') && (
              <button
                onClick={() => {
                  setActiveTab('tools');
                  fetchRepairLastRun();
                  fetchPlatformHealth();
                }}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'tools'
                    ? 'text-white border-b-2 border-purple-500'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-tools"
              >
                🛠️ Tools
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'submissions' && (
          <>
            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Submissions', value: submissions.length, color: 'purple' },
                { label: 'Under Review', value: submissions.filter(s => s.status === 'under_review').length, color: 'yellow' },
                { label: 'Featured', value: submissions.filter(s => s.status === 'featured').length, color: 'green' },
                { label: 'This Week', value: submissions.filter(s => s.week_number === Math.ceil((new Date() - new Date('2026-01-01')) / (7 * 24 * 60 * 60 * 1000))).length, color: 'blue' }
              ].map((stat, i) => (
                <Card key={i} className={`p-4 bg-${stat.color}-900/20 border-${stat.color}-500/30`} style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </Card>
              ))}
            </div>

            {/* Filters */}
            <Card className="p-4 mb-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
              <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Genre</Label>
                  <select
                    value={filters.genre}
                    onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
                    className="w-full mt-1 bg-[#1a1a2e] text-white border border-gray-700 rounded-md px-3 py-2 cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                    data-testid="filter-genre"
                  >
                    <option value="">All Genres</option>
                    {GENRES.map(genre => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-gray-300">Status</Label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="w-full mt-1 bg-[#1a1a2e] text-white border border-gray-700 rounded-md px-3 py-2 cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                    data-testid="filter-status"
                  >
                    <option value="">All Statuses</option>
                    {STATUSES.map(status => (
                      <option key={status} value={status}>{status.replace('_', ' ').toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-gray-300">Week Number</Label>
                  <Input
                    type="number"
                    value={filters.week}
                    onChange={(e) => setFilters({ ...filters, week: e.target.value })}
                    placeholder="Week #"
                    className="bg-gray-900 text-white border-gray-700 mt-1"
                    data-testid="filter-week"
                  />
                </div>

                {/* Phase 9.7.14 — Reviewer workflow filters */}
                <div className="md:col-span-3 lg:col-span-3">
                  <Label className="text-gray-300">Workflow filter</Label>
                  <select
                    value={filters.sort}
                    onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                    className="w-full mt-1 bg-[#1a1a2e] text-white border border-amber-500/40 rounded-md px-3 py-2 cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                    data-testid="filter-workflow"
                  >
                    <option value="newest">🆕 Newest first</option>
                    <option value="trending">🔥 Trending (engagement-weighted)</option>
                    <option value="high-engagement">📈 High engagement</option>
                    <option value="spotlight-candidates">🌟 Spotlight candidates (score ≥ 45)</option>
                    <option value="collabs">🤝 Collabs only</option>
                  </select>
                </div>
                <div className="md:col-span-3 lg:col-span-3">
                  <Label className="text-gray-300">Search (track / artist / genre)</Label>
                  <Input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Type to filter…"
                    className="bg-gray-900 text-white border-gray-700 mt-1"
                    data-testid="filter-search"
                  />
                </div>
              </div>
            </Card>

        {/* Submissions List */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-4" data-testid="submissions-count">
            All Submissions ({filteredSubmissions.length})
          </h2>
          
          <div className="space-y-4">
            {filteredSubmissions.map((submission) => (
              <Card
                key={submission.submission_id}
                className="p-6"
                style={{ background: 'rgba(42, 26, 58, 0.5)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
                data-testid={`submission-${submission.submission_id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
                  {/* Track Info */}
                  <div className="lg:col-span-2">
                    <div className="flex items-start gap-4">
                      <img 
                        src={getCoverUrl(submission)}
                        alt={submission.artist_name}
                        className="w-20 h-20 rounded-lg object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=80&h=80&fit=crop'; }}
                      />
                      <div>
                        <h3 className="text-xl font-bold text-white">{submission.track_title || 'Untitled Track'}</h3>
                        <p className="text-purple-400 font-medium">{submission.artist_name}</p>
                        <p className="text-gray-400 text-sm">{submission.genre} • Week {submission.week_number}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status & Score */}
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Status</p>
                    <div className="flex flex-col gap-1">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(submission.status)}`}>
                        {submission.status.replace('_', ' ').toUpperCase()}
                      </span>
                      {submission.status === 'featured' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-black" data-testid={`featured-badge-${submission.submission_id}`}>
                          🎖️ Featured
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-400 mb-1">Total Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(submission.score || 0)}`}>
                      {submission.score || 0}<span className="text-gray-500 text-lg">/60</span>
                    </p>
                  </div>
                </div>

                {/* Score Breakdown */}
                {submission.scores && (
                  <div className="mb-4 p-4 bg-black/30 rounded-lg">
                    <p className="text-sm text-gray-400 mb-3">Score Breakdown</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {SCORE_CATEGORIES.map(cat => (
                        <div key={cat.key} className="text-center">
                          <p className="text-xs text-gray-500 mb-1">{cat.label}</p>
                          <p className={`text-lg font-bold ${getScoreColor(submission.scores[cat.key] || 0, 10)}`}>
                            {submission.scores[cat.key] || 0}<span className="text-gray-500 text-sm">/10</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {(submission.description || submission.bio) && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-1">Description</p>
                    <p className="text-white text-sm">{submission.description || submission.bio}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadFile('music', submission.music_file)}
                    className="text-white border-purple-500 hover:bg-purple-500/20"
                  >
                    Play Music
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadFile('covers', submission.cover_art)}
                    className="text-white border-purple-500 hover:bg-purple-500/20"
                  >
                    View Cover
                  </Button>
                  {submission.epk_file && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadFile('epks', submission.epk_file)}
                      className="text-white border-purple-500 hover:bg-purple-500/20"
                    >
                      Download EPK
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => openEditModal(submission)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    data-testid={`edit-submission-${submission.submission_id}`}
                  >
                    Score & Review
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleFeatureTrack(submission.submission_id, submission.status)}
                    className={submission.status === 'featured' 
                      ? "bg-yellow-600 hover:bg-yellow-700 text-black"
                      : "bg-green-600 hover:bg-green-700 text-white"
                    }
                    data-testid={`feature-submission-${submission.submission_id}`}
                  >
                    {submission.status === 'featured' ? 'Unfeature' : '✨ Feature'}
                  </Button>
                  {/* Phase 9.7.14 — Quick Spotlight toggle */}
                  <Button
                    size="sm"
                    onClick={() => handleSpotlightTrack(submission.submission_id, submission.status)}
                    className={submission.status === 'spotlight'
                      ? "bg-amber-500 hover:bg-amber-400 text-black"
                      : "bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-700 hover:to-amber-400 text-white"
                    }
                    data-testid={`spotlight-submission-${submission.submission_id}`}
                  >
                    {submission.status === 'spotlight' ? 'Unspotlight' : '🌟 Spotlight'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDeleteTrack(submission.submission_id)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid={`delete-submission-${submission.submission_id}`}
                  >
                    Delete Track
                  </Button>
                </div>

                {/* Phase 9.7.14 — Inline track playback. A&R reviewers can
                    listen + review without opening separate pages. The
                    HTML5 audio element survives scrolling naturally
                    because the queue is one continuous list. */}
                {(() => {
                  const src = getMusicUrl(submission);
                  if (!src) return null;
                  return (
                    <div
                      className="mt-3 rounded-lg border border-amber-500/20 bg-black/30 px-3 py-2"
                      data-testid={`review-player-${submission.submission_id}`}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-amber-200 font-bold mb-1.5">
                        ▶ Listen — A&R Review Player
                      </p>
                      <audio
                        controls
                        preload="none"
                        src={src}
                        className="w-full"
                        style={{ filter: "invert(0.9) hue-rotate(180deg)" }}
                      />
                    </div>
                  );
                })()}

                {/* Admin Notes */}
                {submission.admin_notes && (
                  <div className="mt-4 p-3 bg-purple-900/20 border border-purple-500/30 rounded">
                    <p className="text-sm text-gray-400">Admin Notes:</p>
                    <p className="text-white text-sm">{submission.admin_notes}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-4" data-testid="users-count">
              All Users ({users.length})
            </h2>
            
            <div className="space-y-4">
              {users.map((userItem) => (
                <Card
                  key={userItem.user_id}
                  className="p-6"
                  style={{ background: 'rgba(42, 26, 58, 0.5)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
                  data-testid={`user-${userItem.user_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <img 
                        src={getProfilePhotoUrl(userItem)}
                        alt={userItem.name}
                        className="w-16 h-16 rounded-full object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop'; }}
                      />
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {userItem.artist_name || userItem.name}
                        </h3>
                        <p className="text-gray-400">{userItem.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            userItem.role === 'admin' 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-gray-700 text-white'
                          }`}>
                            {userItem.role?.toUpperCase() || 'ARTIST'}
                          </span>
                          {userItem.badges?.map(badge => (
                            <span key={badge} className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              badge === 'founder' 
                                ? 'bg-gradient-to-r from-yellow-400 to-amber-600 text-black'
                                : badge === 'producer'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-600 text-white'
                            }`}>
                              {badge.replace('_', ' ').toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{userItem.track_count || 0}</p>
                        <p className="text-xs text-gray-400">Tracks</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{userItem.follower_count || 0}</p>
                        <p className="text-xs text-gray-400">Followers</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/artist/${userItem.user_id}`}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-white border-purple-500 hover:bg-purple-500/20"
                          >
                            View Profile
                          </Button>
                        </Link>
                        {BADGE_TYPES.map(badge => (
                          <Button
                            key={badge.key}
                            size="sm"
                            onClick={() => handleToggleBadge(userItem.user_id, badge.key)}
                            className={userItem.badges?.includes(badge.key)
                              ? badge.activeClass
                              : "bg-gray-700 hover:bg-gray-600 text-white"
                            }
                            data-testid={`toggle-${badge.key}-badge-${userItem.user_id}`}
                          >
                            {userItem.badges?.includes(badge.key) ? `${badge.icon} ${badge.label}` : `+ ${badge.label}`}
                          </Button>
                        ))}
                        {userItem.role !== 'admin' && (
                          <>
                            <Button
                              size="sm"
                              onClick={async () => {
                                const target = userItem.role === 'ar' ? 'artist' : 'ar';
                                try {
                                  await api.post(`/admin/user/${userItem.user_id}/role`, { role: target });
                                  toast.success(target === 'ar' ? `Promoted to A&R` : `Unassigned A&R`);
                                  await fetchUsers();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Role update failed');
                                }
                              }}
                              className={userItem.role === 'ar'
                                ? "bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white"
                                : "bg-gray-700 hover:bg-gray-600 text-white"}
                              data-testid={`toggle-ar-role-${userItem.user_id}`}
                            >
                              {userItem.role === 'ar' ? '🎯 A&R (remove)' : '+ A&R'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteUser(userItem.user_id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`delete-user-${userItem.user_id}`}
                            >
                              Delete User
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Spotlight Tab */}
        {activeTab === 'spotlight' && (
          <div className="space-y-8">
            {/* Current Active Spotlight */}
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>🏆</span> Current Active Spotlight
              </h3>
              
              {activeSpotlight && activeSpotlight.spotlight_id ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-white font-semibold text-lg">{activeSpotlight.week_label}</p>
                      <p className="text-gray-400 text-sm">
                        {new Date(activeSpotlight.week_start).toLocaleDateString()} - {new Date(activeSpotlight.week_end).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${activeSpotlight.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-300'}`} data-testid="spotlight-publish-status">
                        {activeSpotlight.is_published ? '🌍 Published' : '🔒 Draft (hidden from public)'}
                      </span>
                      <Link to={`/admin/spotlight/${activeSpotlight.spotlight_id}/ar-review`} data-testid="active-spotlight-manage-ar">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-fuchsia-400/50 text-fuchsia-200 hover:bg-fuchsia-500/10"
                          title="Configure A&R rubric, assign reviewers, view decision-support signals. Does NOT publish."
                        >
                          ⚖️ Manage A&R
                        </Button>
                      </Link>
                      {activeSpotlight.is_published ? (
                        <Button
                          size="sm"
                          onClick={() => handleUnpublishSpotlight(activeSpotlight.spotlight_id)}
                          variant="outline"
                          className="border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10"
                          data-testid="unpublish-spotlight"
                        >
                          Unpublish
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handlePublishSpotlight(activeSpotlight.spotlight_id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="publish-spotlight"
                        >
                          Publish to Public
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {activeSpotlight.winners?.map((winner, idx) => (
                      <div key={winner.submission_id} className="bg-black/30 rounded-lg p-4 border border-purple-500/20">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {winner.position === 1 ? '🥇' : winner.position === 2 ? '🥈' : '🥉'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">#{winner.position} - {winner.track_title || 'Unknown Track'}</p>
                            <p className="text-gray-400 text-sm truncate">{winner.artist_name || 'Unknown Artist'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-4">No active spotlight set. Create one below to display champions on the landing page.</p>
                </div>
              )}
            </Card>

            {/* Create New Spotlight */}
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>✨</span> Create New Spotlight
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={handleAutoSuggestWinners}
                    variant="outline"
                    size="sm"
                    className="border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10"
                    data-testid="auto-suggest-winners"
                  >
                    🤖 Auto-suggest Winners
                  </Button>
                  <Button
                    onClick={fetchSpotlights}
                    variant="outline"
                    size="sm"
                    className="border-purple-500/50 text-purple-300"
                  >
                    Refresh Tracks ({eligibleTracks.length})
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label className="text-gray-300">Week Label</Label>
                  <Input
                    value={spotlightForm.week_label}
                    onChange={(e) => setSpotlightForm({...spotlightForm, week_label: e.target.value})}
                    placeholder="e.g., Week of April 7, 2026"
                    className="bg-black/50 border-purple-500/30 text-white"
                    data-testid="spotlight-week-label"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Week Start</Label>
                  <Input
                    type="date"
                    value={spotlightForm.week_start}
                    onChange={(e) => setSpotlightForm({...spotlightForm, week_start: e.target.value})}
                    className="bg-black/50 border-purple-500/30 text-white"
                    data-testid="spotlight-week-start"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Week End</Label>
                  <Input
                    type="date"
                    value={spotlightForm.week_end}
                    onChange={(e) => setSpotlightForm({...spotlightForm, week_end: e.target.value})}
                    className="bg-black/50 border-purple-500/30 text-white"
                    data-testid="spotlight-week-end"
                  />
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <h4 className="text-white font-medium">Select Winners (from eligible tracks)</h4>
                
                {/* Winner 1 */}
                <div className="flex items-center gap-4">
                  <span className="text-2xl w-10">🥇</span>
                  <div className="flex-1">
                    <Label className="text-gray-300">#1 Winner (Required)</Label>
                    <select
                      value={spotlightForm.winner1}
                      onChange={(e) => setSpotlightForm({...spotlightForm, winner1: e.target.value})}
                      className="w-full bg-[#1a1a2e] border border-purple-500/30 text-white rounded-md p-3 cursor-pointer hover:border-purple-500 focus:border-purple-500 focus:outline-none"
                      style={{ colorScheme: 'dark' }}
                      data-testid="spotlight-winner1"
                    >
                      <option value="" className="bg-[#1a1a2e] text-gray-400">Select track...</option>
                      {eligibleTracks.map((track) => (
                        <option key={track.submission_id} value={track.submission_id} className="bg-[#1a1a2e] text-white py-2">
                          {track.track_title} - {track.display_artist_name || track.artist_name} (Score: {track.score || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Winner 2 */}
                <div className="flex items-center gap-4">
                  <span className="text-2xl w-10">🥈</span>
                  <div className="flex-1">
                    <Label className="text-gray-300">#2 Winner (Optional)</Label>
                    <select
                      value={spotlightForm.winner2}
                      onChange={(e) => setSpotlightForm({...spotlightForm, winner2: e.target.value})}
                      className="w-full bg-[#1a1a2e] border border-purple-500/30 text-white rounded-md p-3 cursor-pointer hover:border-purple-500 focus:border-purple-500 focus:outline-none"
                      style={{ colorScheme: 'dark' }}
                      data-testid="spotlight-winner2"
                    >
                      <option value="" className="bg-[#1a1a2e] text-gray-400">Select track...</option>
                      {eligibleTracks.filter(t => t.submission_id !== spotlightForm.winner1).map((track) => (
                        <option key={track.submission_id} value={track.submission_id} className="bg-[#1a1a2e] text-white py-2">
                          {track.track_title} - {track.display_artist_name || track.artist_name} (Score: {track.score || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Winner 3 */}
                <div className="flex items-center gap-4">
                  <span className="text-2xl w-10">🥉</span>
                  <div className="flex-1">
                    <Label className="text-gray-300">#3 Winner (Optional)</Label>
                    <select
                      value={spotlightForm.winner3}
                      onChange={(e) => setSpotlightForm({...spotlightForm, winner3: e.target.value})}
                      className="w-full bg-[#1a1a2e] border border-purple-500/30 text-white rounded-md p-3 cursor-pointer hover:border-purple-500 focus:border-purple-500 focus:outline-none"
                      style={{ colorScheme: 'dark' }}
                      data-testid="spotlight-winner3"
                    >
                      <option value="" className="bg-[#1a1a2e] text-gray-400">Select track...</option>
                      {eligibleTracks.filter(t => t.submission_id !== spotlightForm.winner1 && t.submission_id !== spotlightForm.winner2).map((track) => (
                        <option key={track.submission_id} value={track.submission_id} className="bg-[#1a1a2e] text-white py-2">
                          {track.track_title} - {track.display_artist_name || track.artist_name} (Score: {track.score || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCreateSpotlight}
                disabled={savingSpotlight}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
                data-testid="create-spotlight-btn"
              >
                {savingSpotlight ? "Creating..." : "Create & Activate Spotlight"}
              </Button>
            </Card>

            {/* Past Spotlights */}
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>📅</span> All Spotlight Weeks
              </h3>
              
              {spotlights.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No spotlight weeks created yet.</p>
              ) : (
                <div className="space-y-3">
                  {spotlights.map((spotlight) => (
                    <div
                      key={spotlight.spotlight_id}
                      className={`p-4 rounded-lg border ${spotlight.is_active ? 'bg-green-900/20 border-green-500/30' : 'bg-black/30 border-purple-500/20'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium flex items-center gap-2">
                            {spotlight.week_label}
                            {spotlight.is_active && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
                            )}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {new Date(spotlight.week_start).toLocaleDateString()} - {new Date(spotlight.week_end).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2 mt-2">
                            {spotlight.winners?.map((w, i) => (
                              <span key={i} className="text-xs text-gray-500">
                                {w.position === 1 ? '🥇' : w.position === 2 ? '🥈' : '🥉'} {w.artist_name || 'Unknown'}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Link to={`/admin/spotlight/${spotlight.spotlight_id}/ar-review`} data-testid={`spotlight-row-manage-ar-${spotlight.spotlight_id}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-fuchsia-400/50 text-fuchsia-200 hover:bg-fuchsia-500/10"
                              title="A&R Review · rubric · reviewers · signals"
                            >
                              ⚖️ A&R
                            </Button>
                          </Link>
                          {!spotlight.is_active && (
                            <Button
                              size="sm"
                              onClick={() => handleActivateSpotlight(spotlight.spotlight_id)}
                              className="bg-green-600 hover:bg-green-500 text-white"
                            >
                              Activate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleDeleteSpotlight(spotlight.spotlight_id)}
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Verification Tab */}
        {activeTab === 'verification' && (
          <div className="space-y-6" data-testid="verification-tab-content">
            <Card className="p-6" style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>✓</span> Verification Requests
                </h3>
                <div className="flex gap-2">
                  {['pending', 'approved', 'rejected', 'all'].map(f => (
                    <button
                      key={f}
                      onClick={() => {
                        setVerificationFilter(f);
                        fetchVerificationRequests(f);
                      }}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        verificationFilter === f
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid={`verification-filter-${f}`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {verificationRequests.length === 0 ? (
                <p className="text-gray-400 text-center py-8" data-testid="verification-empty">
                  No {verificationFilter !== 'all' ? verificationFilter : ''} verification requests.
                </p>
              ) : (
                <div className="space-y-4">
                  {verificationRequests.map((req) => (
                    <div
                      key={req.request_id}
                      className="p-4 bg-black/30 rounded-lg border border-purple-500/20"
                      data-testid={`verification-request-${req.request_id}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-white font-bold text-lg">
                              {req.artist_name || req.user_name || 'Unknown Artist'}
                            </h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                              req.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                              'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {req.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">{req.user_email || ''}</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                            <div>
                              <span className="text-gray-500">Tracks:</span>{' '}
                              <span className="text-white font-medium">{req.track_count ?? 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Followers:</span>{' '}
                              <span className="text-white font-medium">{req.follower_count ?? 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Requested:</span>{' '}
                              <span className="text-white">{req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}</span>
                            </div>
                          </div>
                          {req.reason && (
                            <div className="mt-3 p-2 bg-red-900/20 border border-red-500/30 rounded text-sm">
                              <span className="text-gray-400">Rejection reason:</span>{' '}
                              <span className="text-red-300">{req.reason}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 min-w-[180px]">
                          <Link to={`/artist/${req.user_id}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full text-white border-purple-500 hover:bg-purple-500/20"
                              data-testid={`view-artist-${req.request_id}`}
                            >
                              View Profile
                            </Button>
                          </Link>
                          {req.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApproveVerification(req.request_id)}
                                className="w-full bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-verification-${req.request_id}`}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setRejectingId(req.request_id);
                                  setRejectReason("");
                                }}
                                className="w-full bg-red-600 hover:bg-red-700 text-white"
                                data-testid={`reject-verification-${req.request_id}`}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {rejectingId === req.request_id && (
                        <div className="mt-4 p-3 bg-black/40 border border-red-500/30 rounded-lg" data-testid={`reject-form-${req.request_id}`}>
                          <Label className="text-gray-300">Reason for rejection</Label>
                          <Textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Please explain why this verification request is being rejected..."
                            rows={3}
                            className="mt-1 bg-gray-900 text-white border-gray-700"
                            data-testid={`reject-reason-${req.request_id}`}
                          />
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              onClick={() => handleRejectVerification(req.request_id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`confirm-reject-${req.request_id}`}
                            >
                              Confirm Reject
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setRejectingId(null);
                                setRejectReason("");
                              }}
                              className="text-white border-gray-700"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6" data-testid="tools-tab-content">
            {/* 🎛 Phase 12.1 — Promotion Control Center */}
            <PromotionControlCenter />

            {/* 📱 App Installs by Source — Phase 10.1 placeholder */}
            <AppInstallsBySourceCard />

            {/* 📱 App Promotion Controls — Get the App banner settings */}
            <AppPromotionControlsCard />

            {/* #11 Platform Health — counts, storage, data integrity */}
            <Card
              className="p-6"
              style={{ background: 'rgba(26, 42, 58, 0.3)', borderColor: 'rgba(34, 197, 94, 0.25)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>📈</span> Platform Health
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                    Live counts, soft capacity warnings, and data-integrity checks (broken covers / audio). Soft limits are informational — nothing is enforced at this layer.
                  </p>
                </div>
                <Button
                  onClick={fetchPlatformHealth}
                  disabled={healthLoading}
                  variant="outline"
                  className="text-white border-gray-700"
                  data-testid="platform-health-refresh"
                >
                  {healthLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
              {platformHealth && (
                <div className="space-y-4" data-testid="platform-health-result">
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {Object.entries(platformHealth.counts || {}).map(([k, v]) => {
                      const warnAt = (platformHealth.limits || {})[`${k}_warn_at`];
                      const hot = warnAt && v >= warnAt * 0.8;
                      return (
                        <div
                          key={k}
                          className={`p-3 rounded-lg border ${hot ? 'border-orange-500/60 bg-orange-500/10' : 'border-gray-800 bg-black/30'}`}
                          data-testid={`health-count-${k}`}
                        >
                          <div className="text-[10px] uppercase tracking-wide text-gray-500">{k}</div>
                          <div className="text-lg font-bold text-white">{Number(v).toLocaleString()}</div>
                          {warnAt && (
                            <div className="text-[9px] text-gray-500 mt-0.5">
                              warn at {Number(warnAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div
                      className={`p-3 rounded-lg border ${platformHealth.integrity.broken_covers > 0 ? 'border-yellow-500/40 bg-yellow-500/10' : 'border-green-500/30 bg-green-500/5'}`}
                      data-testid="health-broken-covers"
                    >
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">Broken covers (non-https)</div>
                      <div className="text-lg font-bold text-white">{platformHealth.integrity.broken_covers}</div>
                    </div>
                    <div
                      className={`p-3 rounded-lg border ${platformHealth.integrity.broken_audio > 0 ? 'border-yellow-500/40 bg-yellow-500/10' : 'border-green-500/30 bg-green-500/5'}`}
                      data-testid="health-broken-audio"
                    >
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">Broken audio (non-https)</div>
                      <div className="text-lg font-bold text-white">{platformHealth.integrity.broken_audio}</div>
                    </div>
                    <div
                      className={`p-3 rounded-lg border ${platformHealth.storage.cloudinary_configured ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/50 bg-red-500/10'}`}
                      data-testid="health-cloudinary"
                    >
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">Cloudinary</div>
                      <div className="text-lg font-bold text-white">
                        {platformHealth.storage.cloudinary_configured ? '✅ Configured' : '❌ Missing'}
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Generated at {new Date(platformHealth.generated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </Card>

            {/* Phase 6 Critical: Find Duplicate Emails (scan-only) */}
            <Card
              className="p-6"
              style={{ background: 'rgba(58, 26, 42, 0.3)', borderColor: 'rgba(236, 72, 153, 0.25)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>📧</span> Find Duplicate Emails (Scan-Only)
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                    Surfaces accounts that share the same email (case-insensitive). Each duplicate group lists every account, with the oldest tagged <strong className="text-amber-300">Primary</strong>. Click <strong className="text-pink-200">Merge into primary</strong> on any non-primary row to re-attribute its content to the primary and delete the duplicate. <strong className="text-pink-200">Per-row only</strong> — never auto-merged, always admin-confirmed.
                  </p>
                </div>
                <Button
                  onClick={handleScanDuplicateEmails}
                  disabled={dupeScanRunning}
                  className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold"
                  data-testid="scan-dupes-button"
                >
                  {dupeScanRunning ? 'Scanning…' : 'Find Duplicates'}
                </Button>
              </div>

              {dupeScanResult && (
                <div className="space-y-3" data-testid="scan-dupes-result">
                  <div className="flex flex-wrap gap-2">
                    <div className="px-3 py-2 rounded-lg bg-black/30 border border-gray-800">
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">Duplicate groups</div>
                      <div className="text-lg font-bold text-white" data-testid="scan-dupes-group-count">
                        {(dupeScanResult.duplicate_groups || []).length}
                      </div>
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-pink-500/10 border border-pink-500/40">
                      <div className="text-[10px] uppercase tracking-wide text-pink-300">Total duplicate users</div>
                      <div className="text-lg font-bold text-pink-100" data-testid="scan-dupes-user-count">
                        {(dupeScanResult.duplicate_groups || []).reduce((a, g) => a + (g.count || 0), 0)}
                      </div>
                    </div>
                  </div>

                  {(dupeScanResult.duplicate_groups || []).length === 0 ? (
                    <p className="text-sm text-emerald-300 italic" data-testid="scan-dupes-empty">
                      ✅ No duplicate emails. Your user table is clean.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {dupeScanResult.duplicate_groups.map((g) => (
                        <Card
                          key={g.email_lower}
                          className="p-4 bg-black/30 border-gray-800"
                          data-testid={`dupe-group-${g.email_lower}`}
                        >
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <p className="text-white font-mono text-sm break-all">
                              {g.email_lower}
                            </p>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-200">
                              {g.count} accounts
                            </span>
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-gray-800">
                            <table className="w-full text-xs">
                              <thead className="bg-black/40 text-gray-400 uppercase tracking-wide">
                                <tr>
                                  <th className="px-3 py-2 text-left">User ID</th>
                                  <th className="px-3 py-2 text-left">Display Name</th>
                                  <th className="px-3 py-2 text-left">Artist</th>
                                  <th className="px-3 py-2 text-left">Auth</th>
                                  <th className="px-3 py-2 text-left">Role</th>
                                  <th className="px-3 py-2 text-left">Created</th>
                                  <th className="px-3 py-2 text-left">Action</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-200">
                                {(g.users || []).map((u, idx) => {
                                  const isPrimary = idx === 0;
                                  const primaryUser = (g.users || [])[0];
                                  const dupeLabel = u.artist_name || u.name || u.user_id;
                                  const primaryLabel = primaryUser ? (primaryUser.artist_name || primaryUser.name || primaryUser.user_id) : '';
                                  return (
                                    <tr
                                      key={u.user_id}
                                      className={`border-t border-gray-800 ${isPrimary ? 'bg-amber-500/5' : ''}`}
                                      data-testid={`dupe-row-${u.user_id}`}
                                    >
                                      <td className="px-3 py-2 font-mono text-[10px] text-gray-400">{u.user_id}</td>
                                      <td className="px-3 py-2">{u.name || <em className="text-gray-500">—</em>}</td>
                                      <td className="px-3 py-2">{u.artist_name || <em className="text-gray-500">—</em>}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex flex-wrap gap-1">
                                          {u.has_password && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-500/30 text-blue-200">password</span>}
                                          {u.google_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/30 text-emerald-200">google</span>}
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">{u.role || 'artist'}</td>
                                      <td className="px-3 py-2 text-gray-400">
                                        {u.created_at ? String(u.created_at).slice(0, 10) : '—'}
                                      </td>
                                      <td className="px-3 py-2">
                                        {isPrimary ? (
                                          <span
                                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 font-bold uppercase tracking-wider"
                                            data-testid={`dupe-primary-${u.user_id}`}
                                          >
                                            👑 Primary
                                          </span>
                                        ) : (
                                          <Button
                                            size="sm"
                                            disabled={mergingUserId === u.user_id}
                                            onClick={() => handleMergeDuplicateUser(u.user_id, primaryUser.user_id, dupeLabel, primaryLabel)}
                                            className="bg-pink-600 hover:bg-pink-700 text-white text-xs h-7 px-3 disabled:opacity-50"
                                            title={`Merge ${dupeLabel} into ${primaryLabel}`}
                                            data-testid={`dupe-merge-${u.user_id}`}
                                          >
                                            {mergingUserId === u.user_id ? 'Merging…' : '⮕ Merge into primary'}
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <p className="mt-2 text-[11px] text-gray-500">
                            <span className="text-amber-300">Primary</span> = oldest record (suggested keeper). Merging a duplicate moves submissions, comments, follows, owned crews/clubs/playlists/businesses → primary, then deletes the duplicate. <strong className="text-pink-200">This cannot be undone.</strong>
                          </p>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Phase 6 Part 2: Scan Assets (Rehydrate) */}
            <Card
              className="p-6"
              style={{ background: 'rgba(26, 42, 58, 0.3)', borderColor: 'rgba(59, 130, 246, 0.25)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🔍</span> Rehydrate Broken Assets (Read-Only Scan)
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                    Scans users, submissions, crews, businesses, and playlists for media URLs
                    that are missing or not hosted on Cloudinary (bare local filenames,
                    legacy <code>/api/files/</code> paths, ephemeral preview URLs). Read-only —
                    no DB changes; use this list to guide re-uploads or targeted repairs.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleScanAssets}
                    disabled={scanAssetsRunning}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold"
                    data-testid="scan-assets-button"
                  >
                    {scanAssetsRunning ? 'Scanning…' : 'Run Scan'}
                  </Button>
                </div>
              </div>

              {scanAssetsResult && (
                <div className="space-y-4" data-testid="scan-assets-result">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    {Object.entries(scanAssetsResult.scanned || {}).map(([k, v]) => (
                      <div key={k} className="p-3 rounded-lg bg-black/30 border border-gray-800">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500">{k}</div>
                        <div className="text-lg font-bold text-white">{v}</div>
                      </div>
                    ))}
                    <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/40">
                      <div className="text-[10px] uppercase tracking-wide text-orange-300">Flagged</div>
                      <div className="text-lg font-bold text-orange-200" data-testid="scan-assets-flagged-count">
                        {scanAssetsResult.flagged_count}
                      </div>
                    </div>
                  </div>

                  {scanAssetsResult.flagged?.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400">Filter by kind:</span>
                        {['all', 'user', 'submission', 'crew', 'business', 'playlist'].map((k) => (
                          <button
                            key={k}
                            onClick={() => setScanAssetsKindFilter(k)}
                            className={`text-xs px-2 py-1 rounded-full border ${
                              scanAssetsKindFilter === k
                                ? 'bg-blue-500/20 border-blue-500 text-white'
                                : 'bg-black/30 border-gray-700 text-gray-400 hover:text-white'
                            }`}
                            data-testid={`scan-assets-filter-${k}`}
                          >
                            {k}
                          </button>
                        ))}
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-800">
                        <table className="w-full text-xs">
                          <thead className="bg-black/40 text-gray-400 uppercase tracking-wide">
                            <tr>
                              <th className="px-3 py-2 text-left">Kind</th>
                              <th className="px-3 py-2 text-left">ID</th>
                              <th className="px-3 py-2 text-left">Title</th>
                              <th className="px-3 py-2 text-left">Field</th>
                              <th className="px-3 py-2 text-left">Value</th>
                              <th className="px-3 py-2 text-left">Reason</th>
                              <th className="px-3 py-2 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="text-gray-200">
                            {scanAssetsResult.flagged
                              .filter((f) => scanAssetsKindFilter === 'all' || f.kind === scanAssetsKindFilter)
                              .slice(0, 300)
                              .map((f, i) => {
                                const pageHref = (() => {
                                  if (f.kind === 'submission') return `/track/${f.id}`;
                                  if (f.kind === 'user') return `/artist/${f.id}`;
                                  if (f.kind === 'crew') return `/crews/${f.id}`;
                                  if (f.kind === 'business') return `/businesses/${f.id}`;
                                  if (f.kind === 'playlist') return `/playlist/${f.id}`;
                                  return null;
                                })();
                                const editHref =
                                  f.kind === 'submission' ? `/track/${f.id}/edit` : null;
                                return (
                                  <tr key={`${f.kind}-${f.id}-${f.field}-${i}`} className="border-t border-gray-800" data-testid={`scan-row-${i}`}>
                                    <td className="px-3 py-2 font-medium">{f.kind}</td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-gray-400">{f.id}</td>
                                    <td className="px-3 py-2 max-w-[200px] truncate">{f.title}</td>
                                    <td className="px-3 py-2 text-purple-300">{f.field}</td>
                                    <td className="px-3 py-2 max-w-[220px] truncate text-gray-400">{f.value || <em>missing</em>}</td>
                                    <td className="px-3 py-2 text-orange-300">{f.reason}</td>
                                    <td className="px-3 py-2">
                                      <div className="flex gap-1 flex-wrap">
                                        {pageHref && (
                                          <Link
                                            to={pageHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2 py-0.5 rounded border border-purple-500/40 text-purple-300 hover:bg-purple-500/20 text-[10px]"
                                            data-testid={`scan-row-open-${i}`}
                                          >
                                            Open
                                          </Link>
                                        )}
                                        {editHref && (
                                          <Link
                                            to={editHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2 py-0.5 rounded border border-amber-500/50 text-amber-300 hover:bg-amber-500/20 text-[10px]"
                                            data-testid={`scan-row-reupload-${i}`}
                                          >
                                            Re-upload
                                          </Link>
                                        )}
                                        <button
                                          onClick={handleScanAssets}
                                          className="px-2 py-0.5 rounded border border-green-500/40 text-green-300 hover:bg-green-500/20 text-[10px]"
                                          data-testid={`scan-row-recheck-${i}`}
                                          title="Rescan to mark fixed"
                                        >
                                          Recheck
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        Showing up to 300 rows. Total flagged: {scanAssetsResult.flagged_count}.
                      </p>
                    </>
                  )}
                </div>
              )}
            </Card>

            <Card
              className="p-6"
              style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🛠️</span> Repair Covers
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                    Scans every track and playlist for covers that aren't stored permanently
                    on Cloudinary (missing URL, bare local filename, or non-Cloudinary URL).
                    Repair re-uploads the local file when available and updates the database
                    with a permanent CDN URL.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleScanCovers}
                    disabled={repairScanning || repairRunning}
                    variant="outline"
                    className="text-white border-gray-700"
                    data-testid="scan-covers-button"
                  >
                    {repairScanning ? 'Scanning…' : 'Scan Only (Dry Run)'}
                  </Button>
                  <Button
                    onClick={handleRepairCovers}
                    disabled={repairRunning || repairScanning}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
                    data-testid="repair-covers-button"
                  >
                    {repairRunning ? 'Repairing…' : 'Repair Covers'}
                  </Button>
                </div>
              </div>

              {/* Last repair run indicator */}
              {repairLastRun && (
                <div
                  className="mb-4 p-3 bg-black/30 border border-gray-800 rounded-lg flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
                  data-testid="last-repair-run"
                >
                  <span className="text-gray-400">
                    Last repair run:{' '}
                    <span className="text-white" data-testid="last-repair-timestamp">
                      {new Date(repairLastRun.last_run_at).toLocaleString()}
                    </span>
                  </span>
                  <span className="text-gray-400">
                    Scanned: <span className="text-white font-medium">{repairLastRun.scanned}</span>
                  </span>
                  <span className="text-gray-400">
                    Repaired: <span className="text-green-400 font-medium">{repairLastRun.repaired}</span>
                  </span>
                  <span className="text-gray-400">
                    Failed: <span className="text-red-400 font-medium">{repairLastRun.failed}</span>
                  </span>
                  <span className="text-gray-400">
                    Remaining: <span className="text-purple-300 font-medium">{repairLastRun.more_remaining || 0}</span>
                  </span>
                </div>
              )}

              {repairResult && (
                <div className="mt-4" data-testid="repair-covers-result">
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4">
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-scanned">
                      <p className="text-xs text-gray-400">Scanned</p>
                      <p className="text-2xl font-bold text-white">{repairResult.scanned}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-broken">
                      <p className="text-xs text-gray-400">Broken</p>
                      <p className="text-2xl font-bold text-yellow-400">{repairResult.broken}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-repaired">
                      <p className="text-xs text-gray-400">Repaired</p>
                      <p className="text-2xl font-bold text-green-400">{repairResult.repaired}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-failed">
                      <p className="text-xs text-gray-400">Failed</p>
                      <p className="text-2xl font-bold text-red-400">{repairResult.failed}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-skipped">
                      <p className="text-xs text-gray-400">No local file</p>
                      <p className="text-2xl font-bold text-gray-300">
                        {repairResult.skipped_missing_local_file}
                      </p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg" data-testid="repair-stat-remaining">
                      <p className="text-xs text-gray-400">Remaining</p>
                      <p className="text-2xl font-bold text-purple-300">
                        {repairResult.more_remaining || 0}
                      </p>
                    </div>
                  </div>

                  {repairResult.dry_run && (
                    <p className="text-xs text-purple-300 mb-3">
                      Dry-run results — nothing has been written to the database yet. Click
                      "Repair Covers" to apply the fix.
                    </p>
                  )}
                  {!repairResult.dry_run && repairResult.more_remaining > 0 && (
                    <p className="text-xs text-purple-300 mb-3">
                      {repairResult.more_remaining} broken cover(s) still left after this batch
                      (limit: {repairResult.limit}). Click "Repair Covers" again to continue.
                    </p>
                  )}

                  {Array.isArray(repairResult.details) && repairResult.details.length > 0 && (
                    <div className="border border-gray-800 rounded-lg overflow-hidden">
                      <div className="max-h-80 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-black/60 sticky top-0">
                            <tr className="text-left text-gray-400">
                              <th className="px-3 py-2">Kind</th>
                              <th className="px-3 py-2">Title</th>
                              <th className="px-3 py-2">ID</th>
                              <th className="px-3 py-2">Action</th>
                              <th className="px-3 py-2">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {repairResult.details.map((d, i) => (
                              <tr key={`${d.id}-${i}`} className="border-t border-gray-800">
                                <td className="px-3 py-2 text-gray-300">{d.kind}</td>
                                <td className="px-3 py-2 text-white truncate max-w-[220px]">
                                  {d.title || '—'}
                                </td>
                                <td className="px-3 py-2 text-gray-500 font-mono text-xs">
                                  {d.id}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={
                                      d.action === 'repaired'
                                        ? 'text-green-400'
                                        : d.action === 'failed'
                                        ? 'text-red-400'
                                        : d.action === 'would_repair'
                                        ? 'text-purple-300'
                                        : 'text-gray-400'
                                    }
                                  >
                                    {d.action}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-400 truncate max-w-[280px]">
                                  {d.reason || d.error || ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {repairResult.details_truncated && (
                        <p className="text-xs text-gray-500 px-3 py-2 bg-black/40">
                          Results truncated to first 200 entries.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Edit/Score Modal */}
        {selectedSubmission && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSubmission(null)}>
            <Card 
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" 
              style={{ background: 'rgba(26, 26, 30, 0.98)', borderColor: 'rgba(138, 43, 226, 0.5)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                Review: {selectedSubmission.track_title || selectedSubmission.artist_name}
              </h3>
              <p className="text-purple-400 mb-6">by {selectedSubmission.artist_name}</p>

              <div className="space-y-6">
                {/* Status */}
                <div>
                  <Label className="text-gray-300">Status</Label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full mt-1 bg-[#1a1a2e] text-white border border-gray-700 rounded-md px-3 py-2 cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    {STATUSES.map(status => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scoring */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-300 text-lg">Score Breakdown</Label>
                    <span className={`text-xl font-bold ${getScoreColor(calculateTotalScore(scores))}`}>
                      Total: {calculateTotalScore(scores)}/60
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {SCORE_CATEGORIES.map(cat => (
                      <div key={cat.key} className="p-3 bg-black/30 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-white font-medium">{cat.label}</Label>
                          <span className={`font-bold ${getScoreColor(scores[cat.key] || 0, 10)}`}>
                            {scores[cat.key] || 0}/10
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{cat.description}</p>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={scores[cat.key] || 0}
                          onChange={(e) => setScores({ ...scores, [cat.key]: parseInt(e.target.value) })}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>0</span>
                          <span>10</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <Label className="text-gray-300">Admin Notes</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="mt-1 bg-gray-900 text-white border-gray-700"
                    placeholder="Add internal notes about this submission..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleUpdateSubmission}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
                  >
                    Save Review
                  </Button>
                  <Button
                    onClick={() => setSelectedSubmission(null)}
                    variant="outline"
                    className="flex-1 text-white border-gray-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
